import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle2, XCircle, Handshake, Inbox } from "lucide-react";
import localApi from "@/lib/localApi";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const ICONS = {
  accepted: { Icon: Handshake, cls: "text-emerald-400" },
  estimate_approved: { Icon: CheckCircle2, cls: "text-emerald-400" },
  estimate_declined: { Icon: XCircle, cls: "text-rose-400" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await localApi.get("/marketplace/notifications");
      setItems(data?.notifications || []);
      setUnread(data?.unread || 0);
    } catch (e) { console.debug("notification poll failed", e?.message); }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 30000);
    return () => clearInterval(timer.current);
  }, [load]);

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o && unread > 0) {
      try { await localApi.post("/marketplace/notifications/read"); setUnread(0); } catch (e) { console.debug("mark notifications read failed", e?.message); }
    }
  };

  const go = (link) => {
    setOpen(false);
    if (link) navigate(link);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="notification-bell"
          className="relative h-9 w-9 rounded-lg flex items-center justify-center text-slate-300 hover:bg-slate-800 transition-colors">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-slate-950 text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-slate-900 border-slate-800 text-slate-100" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
          <p className="font-head font-bold text-sm">Notifications</p>
          {items.length > 0 && <span className="text-[11px] text-slate-500">{items.length}</span>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="py-10 text-center text-slate-500" data-testid="no-notifications">
              <Inbox className="h-7 w-7 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
              <p className="text-xs mt-1">You'll be alerted when clients accept or decline.</p>
            </div>
          ) : (
            items.map((n) => {
              const cfg = ICONS[n.type] || { Icon: Bell, cls: "text-slate-400" };
              const Icon = cfg.Icon;
              return (
                <button key={n.id} type="button" data-testid={`notification-item-${n.id}`} onClick={() => go(n.link)}
                  className={`w-full text-left flex gap-3 px-4 py-3 border-b border-slate-800/60 hover:bg-slate-800/50 transition-colors ${n.read ? "" : "bg-slate-800/30"}`}>
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${cfg.cls}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-100">{n.title}</p>
                    <p className="text-xs text-slate-400 leading-snug">{n.body}</p>
                    <p className="text-[11px] text-slate-600 mt-0.5">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <span className="h-2 w-2 rounded-full bg-amber-500 mt-1.5 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
